import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import PageHeader from "../components/PageHeader";
import { fetchPatients } from "../../core/redux/slices/patientSlice";
import { fetchInvoices } from "../../core/redux/slices/invoiceSlice";
import { getPaymentsInRange } from "../../core/api/invoiceService";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

const BillWiseReceiptList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const patients = useSelector((s) => s.patients.list);
  const invoices = useSelector((s) => s.invoices.list);

  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [patientId, setPatientId] = useState("");
  const [receipts, setReceipts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printReceipt, setPrintReceipt] = useState(null);

  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchInvoices());
  }, [dispatch]);

  const patientOptions = patients.map((p) => ({
    value: p.patientID || p.id,
    label: `${p.patientName} — ${p.mobileNumber || ""} (OP: ${p.opNumber || "—"})`,
  }));

  const runSearch = async () => {
    setLoading(true);
    try {
      const out = await getPaymentsInRange(invoices, dateFrom, dateTo);
      setReceipts(out);
    } catch {
      Swal.fire("Error", "Failed to load bill-wise receipts.", "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = (receipts || []).filter((r) => !patientId || r.patientId === patientId);

  const printRow = (r) => {
    setPrintReceipt({
      patientName: r.patientName,
      paymentDate: r.paymentDate,
      paymentMode: r.paymentMode,
      referenceNo: r.referenceNo,
      lines: [{ invoiceNumber: r.invoiceNumber, amount: r.amount }],
      total: r.amount,
    });
    setTimeout(() => window.print(), 0);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <style>{`
          .receipt-print-area { display: none; }
          @media print {
            .header, #sidebar, .on-screen-content { display: none !important; }
            .page-wrapper { margin: 0 !important; padding: 0 !important; }
            .receipt-print-area { display: block; max-width: 600px; margin: 40px auto; font-family: Arial, sans-serif; color: #000; }
            .receipt-print-area table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            .receipt-print-area th, .receipt-print-area td { border: 1px solid #000; padding: 8px 10px; }
            .receipt-print-area .receipt-header { text-align: center; margin-bottom: 20px; }
            .receipt-print-area .meta-row { display: flex; justify-content: space-between; margin-bottom: 6px; }
            .receipt-print-area .total-row td { font-weight: bold; }
          }
        `}</style>

        <div className="on-screen-content">
          <PageHeader
            title="Bill wise Receipt"
            subtitle="Payments received against patient invoices"
            onAdd={() => navigate("/invoices/bill-wise-receipt/new")}
            addLabel="Add New Bill wise Receipt"
          />

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2 align-items-end">
                <div className="col-auto">
                  <label className="form-label small mb-1">From</label>
                  <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className="col-auto">
                  <label className="form-label small mb-1">To</label>
                  <input type="date" className="form-control form-control-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                <div className="col-auto" style={{ minWidth: 220 }}>
                  <label className="form-label small mb-1">Patient</label>
                  <Select
                    classNamePrefix="react-select"
                    options={patientOptions}
                    value={patientOptions.find((o) => o.value === patientId) || null}
                    onChange={(opt) => setPatientId(opt?.value || "")}
                    placeholder="All Patients"
                    isClearable
                    isSearchable
                  />
                </div>
                <div className="col-auto">
                  <button className="btn btn-primary" onClick={runSearch} disabled={loading}>
                    {loading ? "Loading..." : "Search"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card table-list-card">
            <div className="card-body">
              {receipts ? (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Invoice #</th>
                        <th>Patient</th>
                        <th>Mode</th>
                        <th>Reference</th>
                        <th className="text-end">Amount</th>
                        <th style={{ width: 70 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr><td colSpan={7} className="text-center text-muted py-3">No receipts found.</td></tr>
                      ) : filtered.map((r) => (
                        <tr key={r.key}>
                          <td>{formatDate(r.paymentDate)}</td>
                          <td>{r.invoiceNumber}</td>
                          <td>{r.patientName}</td>
                          <td>{r.paymentMode}</td>
                          <td>{r.referenceNo}</td>
                          <td className="text-end">₹{fmt(r.amount)}</td>
                          <td className="text-center">
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => printRow(r)}>
                              Print
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted text-center py-4 mb-0">
                  Select a date range and click <strong>Search</strong> to view bill-wise receipts.
                </p>
              )}
            </div>
          </div>
        </div>

        {printReceipt && (
          <div className="receipt-print-area">
            <div className="receipt-header">
              <h4>Payment Receipt</h4>
            </div>
            <div className="meta-row"><span>Patient: {printReceipt.patientName}</span><span>Date: {formatDate(printReceipt.paymentDate)}</span></div>
            <div className="meta-row"><span>Payment Mode: {printReceipt.paymentMode}</span><span>Reference: {printReceipt.referenceNo || "—"}</span></div>
            <table>
              <thead>
                <tr><th>Invoice #</th><th className="text-end">Amount (₹)</th></tr>
              </thead>
              <tbody>
                {printReceipt.lines.map((l, idx) => (
                  <tr key={idx}><td>{l.invoiceNumber}</td><td className="text-end">{fmt(l.amount)}</td></tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row"><td>Total</td><td className="text-end">{fmt(printReceipt.total)}</td></tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillWiseReceiptList;
